// Bridges the site into Telegram's Mini App webview when opened via a
// `web_app` bot button (see supabase/functions/telegram-poll/index.ts).
// No-ops entirely outside Telegram, so it's safe to call on every route.

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  disableVerticalSwipes?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  colorScheme?: "light" | "dark";
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const isTelegramWebApp = (): boolean => !!window.Telegram?.WebApp;

export function initTelegramWebApp(): void {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return;

  webApp.ready();
  webApp.expand();
  webApp.disableVerticalSwipes?.();

  // Match the mini app chrome to the site theme tokens.
  const styles = getComputedStyle(document.documentElement);
  const bg = styles.getPropertyValue("--background").trim();
  if (bg) {
    const hex = hslToHex(bg);
    if (hex) {
      webApp.setHeaderColor?.(hex);
      webApp.setBackgroundColor?.(hex);
    }
  }

  document.documentElement.classList.add("telegram-webapp");

  // Native back button mirrors browser history.
  const back = webApp.BackButton;
  if (back) {
    const sync = () => (window.location.pathname === "/" ? back.hide() : back.show());
    back.onClick(() => window.history.back());
    sync();
    window.addEventListener("popstate", sync);
  }
}

// Converts a Tailwind CSS `H S% L%` token into a #rrggbb string.
function hslToHex(value: string): string | null {
  const m = value.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!m) return null;
  const h = parseFloat(m[1]);
  const s = parseFloat(m[2]) / 100;
  const l = parseFloat(m[3]) / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}
